import React, { useLayoutEffect, useRef, useState } from 'react'
import { THEME } from '../../constant/color';
import HeadingComponent from '../shared/heading';
import mawgifLogo from '@/assets/img/images.png'

// EBITDA Margin / NET PROFIT MARGIN rows are ratios, not amounts — styled
const MARGIN_ROW_LABELS = ["EBITDA", "EBITDA Margin", "OPERATING PROFIT/(LOSS)", "NET PROFIT MARGIN"];

const Bussiness_Performance_By_Country_Component = ({
  reportTitle = "",
  logoSrc = mawgifLogo,
  blocks = [],
  headerYears = [],
  headerMonths = [],
  headerMeasures = [],
}) => {
  const columns = headerYears.map((yr, idx) => ({
    year: yr,
    period: headerMonths[idx],
    type: headerMeasures[idx],
  }));

  // ── Fit-to-height logic (same approach as Mawgif_Group_Table_Component) ──
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [fontScale, setFontScale] = useState(1);

  useLayoutEffect(() => {
    const fit = () => {
      if (!outerRef.current || !innerRef.current) return;
      const containerHeight = outerRef.current.offsetHeight;
      if (containerHeight <= 0) return;

      setFontScale((prev) => {
        const naturalHeightAtScale1 = innerRef.current.scrollHeight / prev;
        if (!naturalHeightAtScale1) return prev;
        const target = Math.min(1, (containerHeight / naturalHeightAtScale1) * 0.99);
        return Math.abs(target - prev) > 0.005 ? target : prev;
      });
    };

    fit();

    const ro = new ResizeObserver(fit);
    if (outerRef.current) ro.observe(outerRef.current);
    window.addEventListener("resize", fit);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [blocks.length, fontScale]);

  const px = (n) => `${(n * fontScale).toFixed(2)}px`;

  const bw = (n) => Math.max(Math.round(n * fontScale), n >= 2 ? n + 0.5 : n) + "px";
  const border = (n, color) => `${bw(n)} solid ${color}`;

  const cellBorders = (left, right, top, bottom) => ({
    "--cell-bl": left,
    "--cell-br": right,
    "--cell-bt": top,
    "--cell-bb": bottom,
  });

  const isGroupStart = (idx) => idx % 4 === 0;
  const isLastCol = (idx) => idx === columns.length - 1;

  const dataLeftBorder = (idx) => (isGroupStart(idx) ? border(2, "black") : border(1, "#999"));
  const dataRightBorder = (idx) => (isLastCol(idx) ? border(2, "black") : border(1, "#999"));

  const bandLeftBorder = (idx) => (isGroupStart(idx) ? border(2, "black") : "none");
  const bandRightBorder = (idx) => (isLastCol(idx) ? border(2, "black") : "none");

  const headerCellBorders = (idx) =>
    cellBorders(
      isGroupStart(idx) ? border(1.5, "black") : border(1, "black"),
      isLastCol(idx) ? border(2, "black") : border(1, "black"),
      border(1, "black"),
      border(1, "black")
    );

  const isVarianceCol = (idx) => /variance/i.test(headerMeasures[idx] || "");

  // NEW: width of the number "gutter" column that sits visually OUTSIDE
  // the bordered table (no border, transparent bg) on the far left.
  const GUTTER_WIDTH = "18px";

  return (
    <div className="w-full h-full flex flex-col gap-2 biz-perf-country">
      <style>{`
        .biz-perf-country table td[style*="--cell-bg"],
        .biz-perf-country table th[style*="--cell-bg"],
        .biz-perf-country span[style*="--cell-bg"] {
          background-color: var(--cell-bg) !important;
        }
        .biz-perf-country table td[style*="--cell-bl"],
        .biz-perf-country table th[style*="--cell-bl"] {
          border-left: var(--cell-bl) !important;
          border-right: var(--cell-br) !important;
          border-top: var(--cell-bt) !important;
          border-bottom: var(--cell-bb) !important;
        }
      `}</style>
      <HeadingComponent variant="main">{reportTitle}</HeadingComponent>

      <div className="flex flex-row gap-1 w-full flex-1 min-h-0 items-stretch px-2">
        <div ref={outerRef} className="flex-1 min-w-0 min-h-0" style={{ overflow: "hidden", height: "600px" }}>
          <div ref={innerRef}>
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  {/* NEW: gutter header cell — no border, blends with page bg,
                      spans all 3 header rows so it lines up with tbody gutter cells */}
                  <th
                    rowSpan={3}
                    style={{
                      width: GUTTER_WIDTH,
                      border: "none",
                      backgroundColor: "transparent",
                      padding: 0,
                    }}
                  />
                  <th
                    rowSpan={2}
                    style={{
                      width: "160px",
                      ...cellBorders(border(2, "black"), border(1, "black"), border(2, "black"), border(1, "black")),
                      backgroundColor: "#fff",
                      "--cell-bg": "#fff",
                      padding: px(4),
                      verticalAlign: "middle",
                    }}
                  >
                    {logoSrc && (
                      <img
                        src={logoSrc}
                        alt=""
                        style={{ width: "35%", height: "20px", objectFit: "cover", display: "block", margin: "0 auto" }}
                      />
                    )}
                  </th>
                  {columns.map((col, idx) => (
                    <th
                      key={`y-${idx}`}
                      className="text-white"
                      style={{ ...headerCellBorders(idx), padding: px(3), fontSize: px(11), backgroundColor: THEME.colors.blue1, "--cell-bg": THEME.colors.blue1 }}
                    >
                      {col.year}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={`p-${idx}`}
                      className="text-white"
                      style={{ ...headerCellBorders(idx), padding: px(3), fontSize: px(11), backgroundColor: THEME.colors.blue1, "--cell-bg": THEME.colors.blue1 }}
                    >
                      {col.period}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th
                    className="text-white"
                    style={{
                      ...cellBorders(border(2, "black"), border(1, "black"), border(2, "black"), border(1, "black")),
                      textAlign: "center",
                      padding: px(3),
                      fontSize: px(11),
                      backgroundColor: THEME.colors.blue1,
                      "--cell-bg": THEME.colors.blue1,
                    }}
                  >
                    SAR
                  </th>
                  {columns.map((col, idx) => (
                    <th
                      key={`t-${idx}`}
                      className="text-white"
                      style={{ ...headerCellBorders(idx), padding: px(3), fontSize: px(11), backgroundColor: THEME.colors.blue1, "--cell-bg": THEME.colors.blue1 }}
                    >
                      {col.type}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {blocks.map((block, cIdx) => {
                  const rows = block?.rows || [];
                  const isLastCountry = cIdx === blocks.length - 1;
                  // NEW: numbering starts from the 2nd block (e.g. KSA),
                  // "ALL COMPANIES" (index 0) gets no badge.
                  const badgeNumber = cIdx > 0 ? cIdx : null;

                  return (
                    <React.Fragment key={cIdx}>
                      <tr>
                        {/* NEW: gutter cell — badge circle lives here, fully
                            outside the black-bordered table, never clipped
                            since it's a normal table column, not absolute.
                            The badge itself is position:absolute so it does
                            NOT add to the row's natural height — the row
                            stays exactly the size it was before. */}
                        <td
                          style={{
                            border: "none",
                            backgroundColor: "transparent",
                            padding: 0,
                            position: "relative",
                          }}
                        >
                          {badgeNumber !== null && (
                            <span
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: px(20),
                                height: px(20),
                                borderRadius: "50%",
                                border: border(1.5, "black"),
                                backgroundColor: "black",
                                color: "white",
                                fontSize: px(12),
                                fontWeight: 600,
                              }}
                            >
                              {badgeNumber}
                            </span>
                          )}
                        </td>

                        <td
                          style={{
                            width: "160px",
                            backgroundColor: "black",
                            "--cell-bg": "black",
                            color: "white",
                            fontWeight: 700,
                            padding: px(3),
                            fontSize: px(11),
                            ...cellBorders(border(2, "black"), border(1, "black"), border(1, "black"), border(1, "black")),
                            textAlign: "center",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span>{block?.country}</span>
                          </div>
                        </td>
                        {columns.map((_, i) => (
                          <td
                            key={`band-blank-${i}`}
                            style={{
                              backgroundColor: "#fff",
                              "--cell-bg": "#fff",
                              ...cellBorders(
                                bandLeftBorder(i),
                                bandRightBorder(i),
                                border(1, "black"),
                                border(1, "black")
                              ),
                            }}
                          />
                        ))}
                      </tr>

                      {rows.map((row, rIdx) => {
                        const isMargin = MARGIN_ROW_LABELS.includes(row.label);
                        const isLastRowOfSection = rIdx === rows.length - 1;
                        const bottomBorder =
                          isLastRowOfSection && isLastCountry ? border(2, "black") : border(1, "#999");
                        const values = row.values || [];
                        const rowBaseBg = isMargin ? THEME.colors.grayMedium : "#fff";

                        return (
                          <tr key={rIdx}>
                            {/* NEW: blank gutter cell to keep columns aligned */}
                            <td style={{ border: "none", backgroundColor: "transparent", padding: 0 }} />

                            <td
                              style={{
                                fontWeight: isMargin ? 700 : 400,
                                color: "black",
                                padding: px(2),
                                paddingLeft: px(24),
                                ...cellBorders(border(2, "black"), border(1, "black"), "none", bottomBorder),
                                fontSize: px(11),
                                backgroundColor: rowBaseBg,
                                "--cell-bg": rowBaseBg,
                              }}
                            >
                              {row.label}
                            </td>

                            {values.map((value, colIndex) => {
                              const isNegative = typeof value === "string" && value.trim().startsWith("(");
                              const cellBg = isVarianceCol(colIndex) ? THEME.colors.yellowLight : rowBaseBg;
                              return (
                                <td
                                  key={colIndex}
                                  style={{
                                    textAlign: "right",
                                    padding: `${px(1)} ${px(4)}`,
                                    fontSize: px(11),
                                    fontWeight: isMargin ? 700 : 400,
                                    ...cellBorders(dataLeftBorder(colIndex), dataRightBorder(colIndex), "none", bottomBorder),
                                    color: isNegative ? THEME.colors.red : "black",
                                    backgroundColor: cellBg,
                                    "--cell-bg": cellBg,
                                  }}
                                >
                                  {value}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="w-72 shrink-0 h-full" style={{ height: "570px", }}>
          <div className="!border p-4 h-full flex flex-col">
            <h3 className="text-lg font-semibold mb-2">Comments</h3>
            <textarea
              className="w-full flex-1 p-2 border-0 focus:outline-none focus:ring-0 resize-none"
              style={{ textAlign: 'left' }}
              rows={10}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bussiness_Performance_By_Country_Component;